import { memo, useMemo, useCallback, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { useInView } from "react-intersection-observer";
import s from './Table.module.scss';
import SortArrows from '@/shared/ui/Table/SortArrows';
import cx from 'classnames';
import _get from 'lodash/get';
import { useTable, useExpanded, useSortBy } from "react-table";
import { useVirtualizer } from '@tanstack/react-virtual';
import { useBreakpoint } from '@/shared/hooks/useBreakpoint'

const DEFAULT_EXPANDED_TR_HEIGHT = 400;
const DEFAULT_EXPANDED_TR_HEIGHT_MD = 650;
const DEFAULT_TR_HEIGHT = 54;
const DEFAULT_OVERSCAN = 10;

type TableProps = {
  classNameRoot?: string;
  classNameInner?: string;
  columns: any;
  data: any;
  onScrollDown?: (inView: boolean) => void;
  onRowClick?: (...props: any) => void;
  isLoading?: boolean;
  stickyHeader?: boolean;
  withHeader?: boolean;
  withExpanded?: boolean;
  responsiveColspan?: {
    md?: number,
  };
  ExpandedRow?: any;
  LoaderComponent?: any;
  stripedRows?: boolean;
  virtualOptions?: {
    estimateSize?: {
      trHeight?: { default?: number, md?: number, },
      expandedTrHeight?: { default?: number, md?: number }
    },
    scrollElementSelector?: string;
    overscan?: number,
  };
}

const ExpandedRowDefault = (props: any): any => {
  return null;
};

const Table = (props:TableProps) => {
  const {
    data,
    stickyHeader = false,
    columns: columnsProps,
    classNameRoot = '',
    classNameInner = '',
    onScrollDown,
    onRowClick = () => {},
    isLoading = false,
    withHeader = true,
    stripedRows = false,
    withExpanded = false,
    responsiveColspan = {}, // this need to control colspan of mobile
    ExpandedRow = ExpandedRowDefault,
    LoaderComponent = null,
    virtualOptions = {},
  } = props;

  const {
    // by default, access modal overlay scroll area
    scrollElementSelector = '[data-radix-content]',
  } = virtualOptions;
  
  const { ref } = useInView({
    onChange: typeof onScrollDown === 'function' ? onScrollDown : undefined,
  });

  const { isMaxMediaMd } = useBreakpoint()
  const defaultColumn = useMemo(
    () => ({
      width: 'auto',
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    columns,
    rows,
    prepareRow,
  } = useTable(
    {
      data,
      columns: columnsProps,
      defaultColumn
    },
    useSortBy,
    useExpanded,
  );

  // virtualization logic
  const estimateSize = useCallback((index: number) => {
    const row = rows[index];
    const isExpanded = _get(row, 'isExpanded', false);
    const {
      estimateSize: {
        expandedTrHeight: {
          md = DEFAULT_EXPANDED_TR_HEIGHT_MD,
          default: defaultExpandedTrHeight = DEFAULT_EXPANDED_TR_HEIGHT,
        } = {},
        trHeight: {
          default: defaultTrHeight = DEFAULT_TR_HEIGHT,
        } = {},
      } = {},
    } = virtualOptions;

    if (isExpanded) {
      return isMaxMediaMd ? md : defaultExpandedTrHeight;
    }

    return defaultTrHeight;
  }, [rows, isMaxMediaMd, virtualOptions]);

  const overscan = _get(virtualOptions, 'overscan', DEFAULT_OVERSCAN);

  const resolveScrollElement = useCallback((): HTMLElement | null => {
    if (typeof document === 'undefined') {
      return null;
    }

    const target = document.querySelector(scrollElementSelector);
    if (target instanceof HTMLElement) {
      return target;
    }

    const scrollingElement = document.scrollingElement;
    if (scrollingElement instanceof HTMLElement) {
      return scrollingElement;
    }

    if (document.documentElement instanceof HTMLElement) {
      return document.documentElement;
    }

    return document.body ?? null;
  }, [scrollElementSelector]);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: resolveScrollElement,
    estimateSize,
    overscan,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 100 ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0) : 0;


  const colSpan = useMemo(() => {
    if (responsiveColspan) {
      switch (true) {
        case responsiveColspan.md && isMaxMediaMd:
          return responsiveColspan.md;
        default:
          return columns.length;
      }
    }
  }, [columns.length, responsiveColspan, isMaxMediaMd]);

  return (
    <div className={cx(s.root, classNameRoot)}>
      <div className={cx(s.inner, classNameInner)}>
        <table {...getTableProps()}>
          {withHeader ? (
            <thead className={cx({
              [s.stickyHead]: stickyHeader
            })}>
              {headerGroups.map((headerGroup: any) => {
                const { key, ...restHeaderGroup } = headerGroup.getHeaderGroupProps();

                return (
                  <tr key={key} {...restHeaderGroup}>
                    {headerGroup.headers.map((column: any) => {
                      const getSortByToggleProps = _get(column, 'getSortByToggleProps', () => ({}));
                      const isSortedDesc = _get(column, 'isSortedDesc', undefined);
                      const isShow = _get(column, 'show', true);
                      const className = _get(column, 'className', '');
                      const { key: headerKey, ...headerProps } = column.getHeaderProps(getSortByToggleProps);

                      return (
                        // Add the sorting props to control sorting. For this example
                        // we can add them into the header props
                        isShow ?
                          <th key={headerKey} className={className} {...headerProps}
                              style={{width: _get(column, 'width', 'auto')}}>
                            {column.canSort ?
                              <div
                                className={cx('flex items-center cursor-pointer', {[s.sortedTh]: isSortedDesc !== undefined})}>
                                {/* Add a sort direction indicator */}
                                {column.render('Header')}
                                <SortArrows isSortedDesc={isSortedDesc} className="ml-1 table-sort-icon"/>
                              </div>
                              :
                              column.render('Header')}
                          </th> :
                          null
                      );
                    })}
                  </tr>
                )
              })}
            </thead>
          ) : null}
          <tbody 
            {...getTableBodyProps()} 
            style={{ paddingTop, paddingBottom, overflow: 'auto', position: 'relative' }}
          >
            {paddingTop > 0 && (
              <tr className="opacity-0">
                <td style={{ height: `${paddingTop}px` }} colSpan={colSpan} />
              </tr>
            )}
            {!!rows?.length ? (
              virtualRows.map((virtualRow: any) => {
                const row = rows[virtualRow.index];
                prepareRow(row);

                const isExpanded = _get(row, 'isExpanded', false);
                const rowProps = row.getRowProps();
                const { key, ...restRowProps } = rowProps;
                return (
                  <Fragment key={key}>
                    <tr
                      {...restRowProps}
                      onClick={
                        withExpanded ? (row.original as any).onClick : () => {
                          onRowClick(row.original);
                        }
                      }
                      className={cx({
                        [s.isExpanded]: withExpanded && isExpanded,
                        [s.striped]: stripedRows,
                      })}>
                      {row.cells.map((cell: any) => {
                        const isShow = _get(cell, 'column.show', true);
                        const className = _get(cell, 'column.className', '');
                        const { key: cellKey, ...cellProps } = cell.getCellProps();
                        return (
                          isShow ? <td key={cellKey} className={className} {...cellProps} {...(row as any).getToggleRowExpandedProps()} title=''>
                            {cell.render('Cell')}
                          </td>
                            :
                          null
                        );
                      })}
                    </tr>

                    {withExpanded && isExpanded && (
                      <tr ref={rowVirtualizer.measure}>
                        <td colSpan={colSpan}>
                          <ExpandedRow {...row.original}/>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <tr className={s.empty}>
                <td colSpan={colSpan}>
                  {!isLoading ? (
                    <div className="flex justify-center items-center text-gray-065 font-medium">
                      <FormattedMessage id="no.data" />
                    </div>
                  ) : null}
                </td>
              </tr>
            )}

            {paddingBottom > 0 && (
              <tr className="opacity-0">
                <td style={{ height: `${paddingBottom}px` }} colSpan={colSpan}/>
              </tr>
            )}
          </tbody>
          {typeof onScrollDown === "function" ? (
            <tfoot ref={ref}>
              {LoaderComponent && isLoading ? (
                <tr>
                  <td colSpan={colSpan}>
                    {LoaderComponent}
                  </td>
                </tr>
              ) : null}
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
};

export default memo(Table);